import React from 'react';

import Flame from '@/assets/icons/Flame.svg';
import { dateRangeMedWithZoneOffset } from '../../helpers/dates';
import { OccationOfferCard } from '../../helpers/list-page/interfaces/list-card.interface';
import { ILibraryMeetingPlaceDictionary } from '@/lib/fragments/libraryMeetingPlaceDictionary';
import { CardLogo } from './card-logo';
import { FavouriteButton } from '../ui/favourite-button/favourite-button';
import Link from 'next/link';
import { CountdownState } from '@/helpers/countdown';
import { getTagBorderColor } from '@/helpers/tailwindColors';
import {
  useGetLibraryConstantsDictionaryData,
  useTenantConfig,
} from '@/providers/ConfigProvider';

type OfferCardVariant = 'default' | 'home';

interface CardOfferProps {
  card: OccationOfferCard;
  cardIndex: number;
  dictionary?: ILibraryMeetingPlaceDictionary;
  countdownState?: CountdownState;
  fixedWidth?: boolean;
  variant?: OfferCardVariant;
}

const variantStyles = {
  default: {
    card_background:
      'bg-background-primary-light lg:hover:bg-background-secondary',
    logo: 'ml-3 mt-3 md:ml-4 md:mt-4 h-12 w-12 md:h-20 md:w-20 rounded-[12px] sm:rounded-lg',
    content_wrapper: 'mt-3 sm:mt-6',
    content: 'sm:min-h-[170px] px-5 sm:px-6',
    brand: 'text-bodys sm:text-bodyl',
    title: 'text-headings md:text-headingm',
    countdown: 'text-bodys sm:text-bodyl',
    icon: 'h-6 w-6 lg:h-8 lg:w-8',
    tags: 'md:py-2 px-2 md:px-3 rounded-sm md:rounded-md text-bodyxs font-bold md:text-headingxxs',
    punchhole: 'bg-brand-white',
    offerType: 'text-headings md:text-headingm py-6 md:py-8',
    favuoriteButton: 'top-3 right-3 md:top-4 md:right-4 md:h-16 md:w-16',
    favIcon: 'md:h-[32px] md:w-[34px]',
    ticketBg: 'bg-brand-white',
    ticketBorder: 'border-brand-white',
  },
  home: {
    card_background: 'bg-background-primary lg:hover:bg-brand-grey-1',
    logo: 'ml-4 mt-4 md:ml-3 md:mt-3 h-20 w-20 md:h-12 md:w-12 rounded-lg sm:rounded-[12px]',
    content_wrapper: 'mt-6 sm:mt-3',
    content: 'min-h-[170px] sm:min-h-[144px] px-6 sm:px-5',
    brand: 'text-bodyl sm:text-bodys',
    title: 'text-headingm md:text-headings',
    countdown: 'text-bodyl sm:text-bodys',
    icon: 'h-8 w-8 lg:h-6 lg:w-6',
    tags: 'py-2 md:py-0 px-3 md:px-2 rounded-md md:rounded-sm text-headingxxs font-bold md:text-bodyxs',
    punchhole: 'bg-background-primary-light',
    offerType: 'text-headingm md:text-headings py-8 md:py-6',
    favuoriteButton:
      'top-4 right-4 md:top-3 md:right-3 h-16 w-16 md:h-10 md:w-10',
    favIcon: 'h-[32px] w-[32px] md:h-5 md:w-5',
    ticketBg: 'bg-background-secondary',
    ticketBorder: 'border-background-secondary',
  },
};

export const CardOffer = ({
  card,
  cardIndex,
  countdownState = { type: 'none' },
  fixedWidth = false,
  variant = 'default',
}: CardOfferProps) => {
  const config = useTenantConfig();
  const dictionary = useGetLibraryConstantsDictionaryData();
  const s = variantStyles[variant];

  const isLivliFamily =
    config?.NEXT_PUBLIC_IS_MP_LIVLI_FAMILY === 'true' &&
    card?.typeOfOffer?.internalName === 'specialOffer';

  const typeOfOfferClass =
    card?.typeOfOffer?.internalName === 'specialOffer'
      ? 'bg-brand-yellow-1'
      : card?.typeOfOffer?.internalName === 'ikeaFamilyMemberOffer'
        ? 'bg-brand-blue-4 text-text-inverse'
        : 'bg-brand-grey-1';

  const aspectRatio =
    card.logo?.width && card.logo?.height
      ? parseInt(card.logo.width) / parseInt(card.logo.height)
      : undefined;

  const isNearlySquare =
    typeof aspectRatio === 'number' && Math.abs(aspectRatio - 1) < 0.1;

  const renderDateOrCountdown = () => {
    switch (countdownState.type) {
      case 'minutely': {
        const minuteText =
          countdownState.minutes === 1
            ? dictionary?.minuteLeft
            : dictionary?.minutesLeft;
        return (
          <div
            className={`flex items-center ${s.countdown} font-bold text-text-default`}
          >
            <Flame className={`${s.icon}`} viewBox="0 0 32 32" />
            {minuteText?.replace('__', countdownState.minutes?.toString())}
          </div>
        );
      }
      case 'hourly': {
        const hourText =
          countdownState.hours === 1
            ? dictionary?.hourLeft
            : dictionary?.hoursLeft;
        return (
          <div
            className={`flex items-center gap-1 ${s.countdown} font-bold text-text-default`}
          >
            <Flame className={`${s.icon}`} viewBox="0 0 32 32" />
            {hourText?.replace('__', countdownState.hours?.toString())}
          </div>
        );
      }
      default:
        return (
          <div className={`${s.countdown} text-text-default`}>
            {dateRangeMedWithZoneOffset(
              card?.validFrom?.toString(),
              card.validTo?.toString(),
              card.node_locale,
            )}
          </div>
        );
    }
  };
  return (
    <div className={`relative ${fixedWidth ? 'w-[224px]' : 'w-full'}`}>
      <Link
        href={card.slug}
        className={`group overflow-hidden rounded-xl h-full w-full flex flex-col  transition-opacity duration-200 ease-in-out focus:outline focus:outline-3 focus:outline-offset-3 focus:outline-text-light ${fixedWidth ? '!w-[224px]' : 'w-full'} ${s.card_background}`}
      >
        {card?.logo?.url ? (
          <CardLogo
            card={card}
            className={`block ${s.logo} flex items-center justify-center ${!isNearlySquare ? 'border border-brand-grey-2' : ''}`}
          />
        ) : (
          <div className="min-h-20 mt-4"></div>
        )}
        <div className={`${s.content_wrapper} flex-1 flex flex-col`}>
          <div className={`${s.content}`}>
            <div className={`pb-1 ${s.brand} text-text-default`}>
              {card?.tags?.length > 0 && card?.tags?.some((tag) => tag?.tagName === 'Car Parking') ? config?.NEXT_PUBLIC_APP_NAME : card?.brand}
            </div>
            <h2
              className={`pb-1 font-title ${s.title} text-text-default break-words`}
            >
              {card.name}
            </h2>
            <div className="text-bodys sm:text-bodyl text-text-default">
              {renderDateOrCountdown()}
            </div>
            {card?.tags && card?.tags?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {card?.tags?.map((tag) => {
                  const borderColor = getTagBorderColor(tag.colour);
                  return (
                    <span
                      key={tag.id}
                      className={`border-2 ${borderColor} ${s.tags} text-neutral-grey-700 whitespace-nowrap`}
                    >
                      {tag.tagName}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Spacer — absorbs all extra space */}
          <div className="flex-1" />

          {/* Ticket tear divider — sits at the seam between primary and yellow */}
          <div className="relative flex items-center mt-7">
            {/* Left punch-hole */}
            <div
              className={`absolute -left-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full z-10 ${s.punchhole}`}
            />
            {/* Right punch-hole */}
            <div
              className={`absolute -right-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full z-10 ${s.punchhole}`}
            />
          </div>

          <div
            className={`flex-1 flex items-center justify-center text-text-default ${typeOfOfferClass} relative`}
          >
            <div className="absolute top-0 left-0 right-0 border-t-4 border-dotted -translate-y-1/2 border-brand-white" />
            <div className={`font-title ${s.offerType} text-center`}>
              {isLivliFamily ? dictionary?.livliFamily : card.typeOfOffer?.name}
            </div>
          </div>
        </div>
      </Link>
      <FavouriteButton
        id={card.id}
        type="offer"
        className={`absolute z-1 ${s.favuoriteButton}`}
        iconclass={`${s.favIcon}`}
        name={card?.name}
      />
    </div>
  );
};
